import type { FindHit } from "../search/findInOpenedTabs.js";
import { MAX_FIND_ALL_HITS } from "../search/findInOpenedTabs.js";

/**
 * Bottom dock panel for “Find result” (Notepad++-style, open tabs only in WEB shell).
 */
export class FindResultsPane {
  readonly element: HTMLElement;
  private readonly summaryEl: HTMLElement;
  private readonly tbody: HTMLTableSectionElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly contextMenu: HTMLDivElement;
  private readonly clearHistoryBtn: HTMLButtonElement;
  private onNavigate: ((hit: FindHit) => void) | null = null;
  private readonly onDocumentPointerDown: (e: PointerEvent) => void;
  private readonly onDocumentKeyDown: (e: KeyboardEvent) => void;

  constructor(host: HTMLElement) {
    host.replaceChildren();
    this.element = document.createElement("aside");
    this.element.className = "npp-find-results-pane";
    this.element.setAttribute("role", "complementary");
    this.element.setAttribute("aria-label", "Find result");
    this.element.hidden = true;

    const header = document.createElement("div");
    header.className = "npp-find-results-header";

    const title = document.createElement("span");
    title.className = "npp-find-results-title";
    title.textContent = "Find result";

    this.summaryEl = document.createElement("span");
    this.summaryEl.className = "npp-find-results-summary";

    this.closeBtn = document.createElement("button");
    this.closeBtn.type = "button";
    this.closeBtn.className = "npp-find-results-close";
    this.closeBtn.setAttribute("aria-label", "Close find results");
    this.closeBtn.textContent = "×";
    this.closeBtn.addEventListener("click", () => this.hide());

    header.append(title, this.summaryEl, this.closeBtn);

    const wrap = document.createElement("div");
    wrap.className = "npp-find-results-body";

    const table = document.createElement("table");
    table.className = "npp-find-results-table";
    const thead = document.createElement("thead");
    const hr = document.createElement("tr");
    for (const label of ["#", "Line", "File", "Line content"]) {
      const th = document.createElement("th");
      th.textContent = label;
      hr.appendChild(th);
    }
    thead.appendChild(hr);
    this.tbody = document.createElement("tbody");
    table.append(thead, this.tbody);
    wrap.appendChild(table);

    this.element.append(header, wrap);
    this.contextMenu = document.createElement("div");
    this.contextMenu.className = "npp-find-results-context";
    this.contextMenu.hidden = true;
    this.contextMenu.setAttribute("role", "menu");
    this.clearHistoryBtn = document.createElement("button");
    this.clearHistoryBtn.type = "button";
    this.clearHistoryBtn.className = "npp-find-results-context-item";
    this.clearHistoryBtn.setAttribute("role", "menuitem");
    this.clearHistoryBtn.textContent = "Clear history";
    this.clearHistoryBtn.addEventListener("click", () => {
      this.clearContent();
      this.hideContextMenu();
    });
    this.contextMenu.appendChild(this.clearHistoryBtn);
    this.element.appendChild(this.contextMenu);

    this.onDocumentPointerDown = (e: PointerEvent) => {
      if (this.contextMenu.hidden) return;
      const target = e.target as Node | null;
      if (target && this.contextMenu.contains(target)) return;
      this.hideContextMenu();
    };
    this.onDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || this.contextMenu.hidden) return;
      e.preventDefault();
      this.hideContextMenu();
    };
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);
    document.addEventListener("keydown", this.onDocumentKeyDown, true);

    this.element.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.showContextMenu(e.clientX, e.clientY);
    });
    host.appendChild(this.element);
  }

  setNavigateHandler(fn: (hit: FindHit) => void): void {
    this.onNavigate = fn;
  }

  /** Show hits in the bottom pane (N++ “Find result” panel). */
  show(summaryLine: string, hits: readonly FindHit[]): void {
    this.summaryEl.textContent = summaryLine;
    this.tbody.replaceChildren();
    this.element.hidden = false;

    if (!hits.length) {
      const tr = document.createElement("tr");
      tr.className = "npp-find-results-empty";
      const td = document.createElement("td");
      td.colSpan = 4;
      td.textContent = "No matches found.";
      tr.appendChild(td);
      this.tbody.appendChild(tr);
      return;
    }

    hits.forEach((h, idx) => {
      const tr = document.createElement("tr");
      tr.className = "npp-find-results-row";
      tr.tabIndex = 0;
      tr.setAttribute("role", "button");

      const tdN = document.createElement("td");
      tdN.className = "npp-find-results-col-num";
      tdN.textContent = String(idx + 1);

      const tdLine = document.createElement("td");
      tdLine.className = "npp-find-results-col-line";
      tdLine.textContent = String(h.line);

      const tdFile = document.createElement("td");
      tdFile.className = "npp-find-results-col-file";
      tdFile.textContent = h.displayPath;

      const tdCtx = document.createElement("td");
      tdCtx.className = "npp-find-results-col-text";
      tdCtx.textContent = h.preview;

      tr.append(tdN, tdLine, tdFile, tdCtx);

      const go = (): void => {
        this.onNavigate?.(h);
      };
      tr.addEventListener("click", go);
      tr.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      });

      this.tbody.appendChild(tr);
    });

    if (hits.length >= MAX_FIND_ALL_HITS) {
      const tr = document.createElement("tr");
      tr.className = "npp-find-results-cap";
      const td = document.createElement("td");
      td.colSpan = 4;
      td.textContent = `… further hits omitted (limit ${MAX_FIND_ALL_HITS})`;
      tr.appendChild(td);
      this.tbody.appendChild(tr);
    }
  }

  hide(): void {
    this.element.hidden = true;
    this.hideContextMenu();
  }

  clearContent(): void {
    this.summaryEl.textContent = "Cleared";
    this.tbody.replaceChildren();
  }

  destroy(): void {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);
    document.removeEventListener("keydown", this.onDocumentKeyDown, true);
    this.element.remove();
  }

  private showContextMenu(clientX: number, clientY: number): void {
    const hostRect = this.element.getBoundingClientRect();
    this.contextMenu.hidden = false;
    this.contextMenu.style.left = `${Math.max(0, clientX - hostRect.left)}px`;
    this.contextMenu.style.top = `${Math.max(0, clientY - hostRect.top)}px`;
  }

  private hideContextMenu(): void {
    this.contextMenu.hidden = true;
  }
}
